import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import talentLogo2 from "../assets/talentlogo2.png";
import usePageMeta from "../hooks/usePageMeta";
import { BASE_URL } from "../config";
import {
  clearManageContext,
  loadManageContext,
  loadManageSelectedLocations,
  clearManageSelectedLocations,
  saveManageContext,
  saveManageSelectedLocations,
} from "./manageContext";
import { clearManageSession, manageFetch } from "./manageSession";
import { formatAppTime } from "../utils/appDateTime";

/** First 10 chars as YYYY-MM-DD when valid; otherwise null. */
function parseManageDateOnly(raw) {
  if (raw == null || raw === "") return null;
  const s = String(raw).trim().slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return null;
  return s;
}

/** Calendar day for device save + dashboard reload: context shift/start, else today (UTC date). */
function resolveManageScheduleDate(ctx) {
  return (
    parseManageDateOnly(ctx?.shiftDate) ||
    parseManageDateOnly(ctx?.startDate) ||
    new Date().toISOString().slice(0, 10)
  );
}

export default function ManageDevice() {
  usePageMeta("Manage Device");
  const navigate = useNavigate();
  const location = useLocation();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [locations, setLocations] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showUnassignConfirm, setShowUnassignConfirm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [unassigning, setUnassigning] = useState(false);
  const [clientQuery, setClientQuery] = useState("");
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [loadingClients, setLoadingClients] = useState(false);
  const debounceRef = useRef(null);
  const manageContext = useMemo(() => loadManageContext(location), [location]);
  const selectedClientRef = useRef(null);
  const { clientName, clientId, locationName, capabilities, clientDeviceId } = manageContext;

  const initialDeviceScopeRef = useRef(undefined);
  if (initialDeviceScopeRef.current === undefined) {
    const cid = Number(manageContext?.clientId);
    initialDeviceScopeRef.current = {
      clientId: Number.isFinite(cid) && cid > 0 ? cid : null,
      clientDeviceId: manageContext?.clientDeviceId ?? null,
    };
  }

  const activeClientId = useMemo(() => {
    if (selectedClient != null) {
      const sid = Number(selectedClient.id);
      if (Number.isFinite(sid) && sid > 0) return sid;
    }
    const cid = Number(clientId);
    return Number.isFinite(cid) && cid > 0 ? cid : null;
  }, [selectedClient, clientId]);

  const displayClientName = selectedClient?.name || clientName || "Client";

  const persistClientToContext = (client) => {
    selectedClientRef.current = client;
    const next = {
      ...manageContext,
      clientId: client.id,
      clientName: client.name || manageContext.clientName || "Client",
      selectedLocationIds: [],
      selectedLocationClientId: null,
      selectedLocationDeviceId: null,
    };
    saveManageContext(next);
  };

  const loadClientLocations = async (clientIdToLoad, ctx = manageContext) => {
    const cid = Number(clientIdToLoad);
    if (!Number.isFinite(cid) || cid <= 0) return;

    const initial = initialDeviceScopeRef.current || {};
    const safeCtx = ctx || {};

    setLoading(true);
    setError("");
    setLocations([]);
    setSelectedIds(new Set());

    const locParams = new URLSearchParams({ clientId: String(cid) });
    const rowDeviceId = safeCtx.clientDeviceId ?? initial.clientDeviceId;
    const deviceParams = new URLSearchParams({
      clientId: String(cid),
      ...(safeCtx.locationName ? { location: safeCtx.locationName } : {}),
      ...(safeCtx.shiftDate ? { shiftDate: safeCtx.shiftDate } : {}),
      ...(safeCtx.startDate ? { from: safeCtx.startDate } : {}),
      ...(rowDeviceId ? { clientDeviceId: String(rowDeviceId) } : {}),
      ...(safeCtx.browserDeviceGuid ? { browserDeviceGuid: String(safeCtx.browserDeviceGuid) } : {}),
    });

    try {
      const [locationRes, deviceRes] = await Promise.all([
        manageFetch(`${BASE_URL}/api/jobs/manage/device-locations?${locParams}`),
        manageFetch(`${BASE_URL}/api/jobs/manage/device?${deviceParams}`),
      ]);
      if (!locationRes.ok) throw new Error(`Failed to load locations (${locationRes.status})`);
      if (!deviceRes.ok) throw new Error(`Failed to load device (${deviceRes.status})`);
      const rows = await locationRes.json();
      const devicePayload = await deviceRes.json();
      const normalized = Array.isArray(rows) ? rows : [];
      const sessionSelectedIds = loadManageSelectedLocations({
        clientId: cid,
        clientDeviceId: safeCtx.clientDeviceId || rowDeviceId || null,
      });

      setLocations(normalized);
      const availableIds = new Set(normalized.map((loc) => String(loc.id)));
      const nextSelected = sessionSelectedIds.filter((id) => availableIds.has(id));
      setSelectedIds(new Set(nextSelected));
      if (devicePayload?.data?.clientDeviceId && !safeCtx.clientDeviceId) {
        saveManageContext({
          ...safeCtx,
          clientId: cid,
          clientDeviceId: devicePayload.data.clientDeviceId,
        });
      }
    } catch (err) {
      setError(err?.message || "Failed to load locations");
      if (safeCtx.locationName) {
        const fallback = [{ id: "fallback-1", name: safeCtx.locationName, label: safeCtx.locationName }];
        setLocations(fallback);
        setSelectedIds(new Set(["fallback-1"]));
      } else {
        setLocations([]);
        setSelectedIds(new Set());
      }
    } finally {
      setLoading(false);
    }
  };

  const loadClients = async (search = "") => {
    setLoadingClients(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set("search", search);
      const response = await manageFetch(
        `${BASE_URL}/api/jobs/manage/device-clients?${params.toString()}`,
      );
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.message || "Failed to load clients");
      }
      const rows = Array.isArray(payload?.data) ? payload.data : [];
      setClients(rows);
    } catch (err) {
      setError(err?.message || "Failed to load clients");
    } finally {
      setLoadingClients(false);
    }
  };

  useEffect(() => {
    const cid = Number(manageContext.clientId);
    if (!Number.isFinite(cid) || cid <= 0) return;
    const initialClient = { id: cid, name: manageContext.clientName || "Client" };
    selectedClientRef.current = selectedClientRef.current || initialClient;
    setSelectedClient((prev) => prev ?? (selectedClientRef.current || initialClient));
  }, [manageContext.clientId, manageContext.clientName]);

  useEffect(() => {
    loadClients();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      loadClients(clientQuery.trim());
    }, 250);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [clientQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!activeClientId) {
      if (locationName) {
        setLocations([{ id: "fallback-1", name: locationName, label: locationName }]);
        setSelectedIds(new Set(["fallback-1"]));
      } else {
        setLocations([]);
        setSelectedIds(new Set());
      }
      return;
    }
    void loadClientLocations(activeClientId, manageContext);
  }, [
    activeClientId,
    locationName,
    manageContext.shiftDate,
    manageContext.startDate,
    manageContext.clientDeviceId,
    manageContext.browserDeviceGuid,
  ]);

  const formatTime = (date) => formatAppTime(date);

  const toggleLocation = (id) => {
    const key = String(id);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleSave = async () => {
    if (capabilities?.canManageDevice === false || !activeClientId || saving) return;
    if (selectedIds.size === 0) {
      setError("Please select at least one location.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const scheduleDate = resolveManageScheduleDate(manageContext);
      const response = await manageFetch(`${BASE_URL}/api/jobs/manage/device`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          clientId: activeClientId,
          shiftDate: scheduleDate,
          from: scheduleDate,
          selectedLocationIds: [...selectedIds],
          clientDeviceId: manageContext.clientDeviceId || null,
          browserDeviceGuid: manageContext.browserDeviceGuid || null,
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.message || `Failed to save device (${response.status})`);
      }
      const nextManageContext = {
        ...manageContext,
        clientId: activeClientId,
        clientName: displayClientName,
        locationName: "",
        shiftId: null,
        timesheetId: null,
        shiftDetails: {},
        timesheetDetails: {},
        shiftDate: scheduleDate,
        startDate: scheduleDate,
        endDate: scheduleDate,
        clientDeviceId: payload?.data?.clientDeviceId || manageContext.clientDeviceId || null,
        selectedLocationIds: payload?.data?.selectedLocationIds || [...selectedIds],
        selectedLocationClientId: activeClientId,
        selectedLocationDeviceId: payload?.data?.clientDeviceId || manageContext.clientDeviceId || null,
      };
      saveManageContext(nextManageContext);
      saveManageSelectedLocations({
        clientId: activeClientId,
        clientDeviceId: nextManageContext.selectedLocationDeviceId,
        selectedLocationIds: [...selectedIds],
      });
      navigate("/manage/signin", {
        replace: true,
        state: { manageContext: nextManageContext },
      });
    } catch (err) {
      setError(err?.message || "Failed to save device");
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmUnassign = async () => {
    if (unassigning) return;
    setUnassigning(true);
    setError("");
    try {
      const params = new URLSearchParams({
        clientId: String(activeClientId || ""),
        ...(locationName ? { location: locationName } : {}),
        ...(manageContext.shiftDate ? { shiftDate: manageContext.shiftDate } : {}),
        ...(manageContext.startDate ? { from: manageContext.startDate } : {}),
      });
      const response = await manageFetch(`${BASE_URL}/api/jobs/manage/device?${params.toString()}`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          selectedLocationIds: [...selectedIds],
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.message || `Failed to unassign device (${response.status})`);
      }
      setShowUnassignConfirm(false);
      clearManageSelectedLocations({
        clientId: activeClientId,
      });
      clearManageSession();
      clearManageContext();
      navigate("/device", {
        replace: true,
        state: { skipAutoRedirect: true },
      });
    } catch (err) {
      setError(err?.message || "Failed to unassign device");
    } finally {
      setUnassigning(false);
    }
  };

  const handleLogoClick = () => {
    const urlParams = new URLSearchParams(location.search);
    if (!urlParams.has("clientId") && !urlParams.has("name") && !urlParams.has("client")) {
      navigate("/device");
      return;
    }
    const checkinParams = new URLSearchParams();
    if (clientId) checkinParams.set("clientId", String(clientId));
    if (manageContext.shiftDate) checkinParams.set("shiftDate", manageContext.shiftDate);
    if (manageContext.startDate) checkinParams.set("from", manageContext.startDate);
    if (manageContext.endDate) checkinParams.set("to", manageContext.endDate);
    if (clientName) checkinParams.set("client", clientName);
    if (manageContext.locationName) checkinParams.set("location", manageContext.locationName);
    
    const forceLoginVal = urlParams.get("forceLogin") || "1";
    checkinParams.set("forceLogin", forceLoginVal);

    navigate(`/checkin?${checkinParams.toString()}`);
  };

  return (
    <div className="h-screen bg-gray-50 flex flex-col overflow-hidden">
      <div className="flex justify-between items-start p-4">
        <div className="cursor-pointer" onClick={handleLogoClick}>
          <img src={talentLogo2} alt="Talent Logo" className="h-12 w-auto" />
        </div>
        <div className="text-right">
          <div className="text-xl font-bold text-gray-800">{formatTime(currentTime)}</div>
          <div className="text-sm text-gray-600">{displayClientName}</div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center px-4 pb-10">
        <div className="w-full max-w-md">
          <h1 className="text-center text-2xl text-gray-700 mb-4">Manage Device</h1>
          <p className="text-xs text-gray-600 mb-3 text-center">
            Locations selected here will be shown in the time tracker. Only shifts for selected locations can be checked in to with this device.
          </p>

          <div className="mb-4">
            <label className="block text-xs text-gray-500 mb-1">Client*</label>
            <div className="border-b border-gray-300">
              <div className="flex items-center gap-3">
                <input
                  value={selectedClient ? selectedClient.name : clientQuery}
                  onChange={(e) => {
                    setSelectedClient(null);
                    selectedClientRef.current = null;
                    setClientQuery(e.target.value);
                  }}
                  placeholder="Search client"
                  className="w-full bg-transparent py-1 text-sm text-gray-800 outline-none focus:border-gray-500"
                />
                {selectedClient ? (
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedClient(null);
                      selectedClientRef.current = null;
                      setClientQuery("");
                      setLocations([]);
                      setSelectedIds(new Set());
                    }}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    x
                  </button>
                ) : null}
              </div>
            </div>
            {!selectedClient ? (
              <div className="max-h-36 overflow-y-auto border-b border-gray-300 bg-white">
                {loadingClients ? (
                  <div className="p-2 text-xs text-gray-500">Loading clients...</div>
                ) : null}
                {!loadingClients &&
                  clients.map((client) => (
                  <button
                      key={client.id}
                      type="button"
                      onClick={() => {
                        setSelectedClient(client);
                        selectedClientRef.current = client;
                        setClientQuery("");
                        persistClientToContext(client);
                        void loadClientLocations(client.id, {
                          ...manageContext,
                          clientId: client.id,
                          clientName: client.name || manageContext.clientName || "Client",
                        });
                      }}
                      className="block w-full border-t border-gray-100 px-2 py-2 text-left text-sm hover:bg-gray-100"
                    >
                      {client.name}
                    </button>
                  ))}
              </div>
            ) : null}
          </div>

          <div className="mb-6">
            <label className="block text-xs text-gray-500 mb-1">Locations*</label>

            {loading && <div className="text-xs text-gray-500 py-2">Loading locations...</div>}
            {error && <div className="text-xs text-red-600 py-2">{error}</div>}

            <div className="space-y-1 max-h-36 overflow-y-auto">
              {locations.map((loc) => {
                const id = String(loc.id);
                const label = loc.label || loc.name || `Location ${id}`;
                return (
                  <label key={id} className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(id)}
                      onChange={() => toggleLocation(id)}
                      className="h-3.5 w-3.5 accent-[#DC1F00]"
                    />
                    <span>{label}</span>
                  </label>
                );
              })}
            </div>
          </div>

          <button
            type="button"
            onClick={handleSave}
            disabled={capabilities?.canManageDevice === false || saving || !activeClientId}
            className="w-full text-[#DC1F00] text-sm font-medium py-1 hover:text-[#b91900] transition-colors disabled:opacity-50"
          >
            {saving ? "SAVING..." : "SAVE"}
          </button>

          <button
            type="button"
            onClick={() => setShowUnassignConfirm(true)}
            disabled={unassigning}
            className="w-full text-gray-700 text-sm font-medium py-1 hover:text-black transition-colors disabled:opacity-50"
          >
            {unassigning ? "UNASSIGNING..." : "UNASSIGN DEVICE"}
          </button>

          <button
            type="button"
            onClick={() =>
              navigate("/manage/dashboard", {
                state: {
                  manageContext: {
                    ...manageContext,
                    clientId: activeClientId ?? manageContext.clientId,
                    clientName: displayClientName,
                    selectedLocationIds: [],
                    selectedLocationClientId: null,
                    selectedLocationDeviceId: null,
                  },
                },
              })
            }
            className="w-full text-gray-700 text-sm font-medium py-1 hover:text-black transition-colors"
          >
            CANCEL
          </button>
        </div>
      </div>

      {showUnassignConfirm && (
        <div className="fixed inset-0 z-50 bg-black/35 flex items-center justify-center px-4">
          <div className="w-full max-w-sm rounded bg-white shadow-xl p-4">
            <h2 className="text-xl text-gray-700 mb-3">Unassign Device</h2>
            <p className="text-sm text-gray-600 mb-4">
              Unassigning will result in staff not being able to check in or check out at this check point. Are you sure you would like to continue?
            </p>

            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleConfirmUnassign}
                className="px-4 py-1 text-sm bg-red-100 text-[#DC1F00] rounded hover:bg-red-200 transition-colors"
              >
                YES
              </button>
              <button
                type="button"
                onClick={() => setShowUnassignConfirm(false)}
                className="px-4 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
              >
                NO
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
